// aotoken/multisig-react/components/multisig/manageMultisig.js

import React, { useState, useEffect, useContext } from "react";
import { ethers } from "ethers";
import Web3 from "web3";
import { WalletContext } from "@dsc/ui/token";
import { contractABI, contractAddress } from "../../contractConfig";
import "../../multisig.css";

// -------------------------------------------------------------
// Setup: Read-only RPC provider
// -------------------------------------------------------------
const RPC_URL = "https://polygon-amoy.drpc.org";
const readOnlyWeb3 = new Web3(new Web3.providers.HttpProvider(RPC_URL));

// -------------------------------------------------------------
// Subcomponent: WalletStatus
// -------------------------------------------------------------
const WalletStatus = ({ contractOwner, connectedAccount }) => {
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    if (connectedAccount && contractOwner) {
      setIsOwner(
        connectedAccount.toLowerCase() === contractOwner.toLowerCase()
      );
    } else {
      setIsOwner(false);
    }
  }, [connectedAccount, contractOwner]);

  return (
    <p style={{ margin: "6px 0" }}>
      <strong>Connected Wallet:</strong>{" "}
      {connectedAccount || "Not connected"}{" "}
      {connectedAccount && (
        <>
          {isOwner ? (
            <span style={{ color: "green", marginLeft: "8px" }}>
              ✅ You are the collection owner
            </span>
          ) : (
            <span style={{ color: "red", marginLeft: "8px" }}>
              ❌ You are not the owner
            </span>
          )}
        </>
      )}
    </p>
  );
};

// -------------------------------------------------------------
// Main component: ManageMultisig
// -------------------------------------------------------------
const ManageMultisig = () => {
  const { address: connectedAccount, signer } = useContext(WalletContext);

  const [newSigner, setNewSigner] = useState("");
  const [requiredSignatures, setRequiredSignatures] = useState(2);
  const [message, setMessage] = useState("");
  const [signerList, setSignerList] = useState([]);
  const [owner, setOwner] = useState("");
  const [loading, setLoading] = useState(false);

  // -------------------------------------------------------------
  // Fetch contract state (read-only via RPC)
  // -------------------------------------------------------------
  const fetchContractState = async () => {
    try {
      const contract = new readOnlyWeb3.eth.Contract(contractABI, contractAddress);

      const contractOwner = await contract.methods.owner().call();
      setOwner(contractOwner);

      const signers = await contract.methods.getSigners().call();
      setSignerList(signers);

      const sigs = await contract.methods.requiredSignatures().call();
      setRequiredSignatures(Number(sigs));
    } catch (err) {
      console.error("Fetch state error:", err);
      setMessage("⚠️ Failed to load contract data");
    }
  };

  useEffect(() => {
    fetchContractState();
  }, []);

  // -------------------------------------------------------------
  // Send write transactions via signer (MetaMask)
  // -------------------------------------------------------------
  const sendTransaction = async (method, ...args) => {
    setLoading(true);
    setMessage("");
    try {
      if (!signer)
        throw new Error("Wallet not connected. Please click Auth Login in the header.");

      const contract = new ethers.Contract(contractAddress, contractABI, signer);
      const tx = await contract[method](...args);

      setMessage(`⏳ Transaction pending: ${tx.hash}`);
      await tx.wait();

      setMessage(`✅ ${method} executed successfully! Hash: ${tx.hash}`);
      fetchContractState(); // Refresh state after success
    } catch (error) {
      console.error(`Error executing ${method}:`, error);
      setMessage(
        error?.reason ||
          error?.message ||
          `Error executing ${method}. See console for details.`
      );
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------
  // Wrapper functions for contract methods
  // -------------------------------------------------------------
  const addSigner = () => {
    if (!newSigner) return setMessage("Please enter a signer address.");
    sendTransaction("addSigner", newSigner);
    setNewSigner("");
  };

  const removeSigner = (signerToRemove) =>
    sendTransaction("removeSigner", signerToRemove);

  const updateRequiredSignatures = () =>
    sendTransaction("setRequiredSignatures", requiredSignatures);

  // -------------------------------------------------------------
  // Warning: owner not in signer list
  // -------------------------------------------------------------
  const showWarning =
    connectedAccount &&
    owner &&
    connectedAccount.toLowerCase() === owner.toLowerCase() &&
    !signerList.some((s) => s.toLowerCase() === connectedAccount.toLowerCase());

  // -------------------------------------------------------------
  // UI
  // -------------------------------------------------------------
  return (
    <div className="container" style={{ marginBottom: "40px" }}>
      <h2>Manage Signers</h2>

      <p>
        <strong>Collection Owner:</strong>{" "}
        {owner ? (
          <a
            href={`https://amoy.polygonscan.com/address/${owner}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#007bff", textDecoration: "underline" }}
          >
            {owner}
          </a>
        ) : (
          "Loading..."
        )}
      </p>

      {/* ✅ Insert WalletStatus here */}
      <WalletStatus contractOwner={owner} connectedAccount={connectedAccount} />

      <p>
        <strong>Note:</strong>{" "}
        <em>
          Only the collection owner can add or remove signers. A warning will appear
          if you are the owner but not in the signer list.
        </em>
     </p>

      {showWarning && (
        <div
          style={{
            background: "#fdd",
            color: "#900",
            padding: "10px",
            borderRadius: "8px",
            marginBottom: "12px",
          }}
        >
          ⚠️ You are the collection owner but not in the signer list. You must add yourself as a
          signer to approve mint requests.
        </div>
      )}

      {/* Add signer */}
      <div style={{ marginTop: "16px" }}>
        <input
          type="text"
          placeholder="New Signer Address"
          value={newSigner}
          onChange={(e) => setNewSigner(e.target.value)}
          className="input-field"
          style={{ width: "50%", marginRight: "10px" }}
        />
        <button
          onClick={addSigner}
          disabled={loading || !newSigner}
          className="primary-btn"
        >
          {loading ? "Processing..." : "Add Signer"}
        </button>
      </div>

      {/* Signer list */}
      <h3 style={{ marginTop: "24px" }}>Current Signers</h3>
      <ul className="signer-list">
        {signerList.length > 0 ? (
          signerList.map((signer, i) => (
            <li className="signer-item" key={i}>
              {signer}
              <button
                onClick={() => removeSigner(signer)}
                disabled={loading}
                className="secondary-btn"
              >
                Remove
              </button>
            </li>
          ))
        ) : (
          <li>No signers found</li>
        )}
      </ul>

      {/* Required signatures */}
      <h3 style={{ marginTop: "24px" }}>Required Signatures</h3>
      <div>
        <input
          type="number"
          min="2"
          value={requiredSignatures}
          onChange={(e) => setRequiredSignatures(Number(e.target.value))}
          className="input-field"
          style={{ width: "40px", marginRight: "10px" }}
        />
        <button
          onClick={updateRequiredSignatures}
          disabled={loading}
          className="primary-btn"
        >
          {loading ? "Updating..." : "Update Required Signatures"}
        </button>
      </div>

      {/* Status message */}
      {message && (
        <p
          style={{
            marginTop: "16px",
            color: message.includes("⚠️") ? "red" : "#333",
          }}
        >
          {message}
        </p>
      )}
    </div>
  );
};

export default ManageMultisig;